function createOrderMarkers({ enabled = false, mode = 'production', getPrivacy, makeGroupId, now = Date.now } = {}) {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const used = new Set();
  const allowed = () => { try { return getPrivacy?.()?.analyticsProcessingAllowed?.() === true; } catch { return false; } };
  return {
    prepare(items, cart) {
      const unchanged = reason => ({ items, status: reason });
      if (!enabled) return unchanged('disabled');
      if (!allowed()) return unchanged('consent_unavailable_or_denied');
      try {
        if (!['production', 'qa'].includes(mode) || !Array.isArray(items) || ![1, 2].includes(items.length)
          || items.some(x => !Number.isSafeInteger(x.id) || x.id <= 0 || x.quantity !== 1 || x.properties)
          || new Set(items.map(x => x.id)).size !== items.length) return unchanged('invalid_items');
        const group = makeGroupId?.();
        const instant = now();
        if (!uuid.test(group ?? '') || used.has(group) || used.size >= 1000 || !Number.isSafeInteger(instant)
          || instant < 0 || (cart.items ?? []).some(x => x.properties?._ah_travel_pair_group === group)) return unchanged('identifier_unavailable');
        const created = new Date(instant).toISOString();
        const marked = items.map((x, i) => ({ ...x, properties: {
          _ah_travel_pair_schema: '1', _ah_travel_pair_group: group,
          _ah_travel_pair_role: i === 0 ? 'main' : 'second', _ah_travel_pair_mode: mode,
          _ah_travel_pair_created_at: created, _ah_travel_pair_bag_count: String(items.length),
          _ah_travel_pair_variant: String(x.id),
        } }));
        if (!allowed()) return unchanged('consent_changed');
        used.add(group);
        return { items: marked, status: 'prepared' };
      } catch { return unchanged('marker_failed'); }
    },
    readback(prepared, before, after) {
      if (prepared.status !== 'prepared') return { status: prepared.status, verifiedItems: 0 };
      const quantity = (cart, item) => cart.items.filter(x => String(x.variant_id) === String(item.id)
        && Object.entries(item.properties).every(([key, value]) => x.properties?.[key] === value))
        .reduce((sum, x) => sum + x.quantity, 0);
      const verifiedItems = prepared.items.filter(item => quantity(after, item) - quantity(before, item) === 1).length;
      return { status: verifiedItems === prepared.items.length ? 'cart_markers_verified' : 'cart_markers_missing_or_partial', verifiedItems };
    },
  };
}
/** Shopify-hosted only. This module is not loaded by the local demo page. */
const ORDER_ATTRIBUTION_REVIEW = Object.freeze({ enabled: true, mode: 'production' });
window.createShopifyAjaxAdapter = function createShopifyAjaxAdapter({ root, fetchImpl = fetch, attribution = ORDER_ATTRIBUTION_REVIEW }) {
  const markers = createOrderMarkers({ ...attribution, getPrivacy: attribution.getPrivacy ?? (() => window.Shopify?.customerPrivacy), makeGroupId: attribution.makeGroupId ?? (() => window.crypto.randomUUID()) });
  if (root !== '/' && !/^\/[a-z]{2}(?:-[a-z]{2})?\/$/i.test(root)) throw new Error('A valid Shopify routes.root is required');

  const request = async (path, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetchImpl(`${root}${path}`, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      let data;
      try { data = await response.json(); } catch { data = null; }
      return { ok: response.ok, status: response.status, data };
    } finally { clearTimeout(timeout); }
  };

  const cart = async () => {
    const result = await request('cart.js');
    if (!result.ok || !result.data || !Array.isArray(result.data.items)) throw new Error(`Cart read failed (${result.status})`);
    if (result.data.currency !== 'EGP') throw new Error(`Unexpected cart currency: ${result.data.currency ?? 'missing'}`);
    return result.data;
  };

  const validate = (selections) => {
    if (!Array.isArray(selections) || selections.length < 1 || selections.length > 2) throw new Error('Choose one travel bag and at most one second bag');
    const productIds = new Set();
    return selections.map((selection) => {
      const { handle, productId, variantId, expectedPriceMinor } = selection;
      if (!/^[a-z0-9-]+$/.test(handle) || !/^gid:\/\/shopify\/Product\/\d+$/.test(productId) || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(variantId) || !Number.isSafeInteger(expectedPriceMinor) || expectedPriceMinor < 0) throw new Error('Invalid exact product selection');
      if (productIds.has(productId)) throw new Error('The same product cannot fill both slots');
      productIds.add(productId);
      return { handle, productId, variantId, numericVariantId: Number(variantId.split('/').at(-1)), expectedPriceMinor };
    });
  };

  const preflight = async (selections) => {
    const checked = validate(selections);
    await cart(); // Confirms the shopper's presentment currency before showing a total.
    const items = await Promise.all(checked.map(async (selection) => {
      const result = await request(`products/${selection.handle}.js`);
      if (!result.ok || !result.data) throw new Error(`Product read failed: ${selection.handle} (${result.status})`);
      if (String(result.data.id) !== selection.productId.split('/').at(-1)) throw new Error(`Product identity changed: ${selection.handle}`);
      const variant = result.data.variants?.find((item) => item.id === selection.numericVariantId);
      if (!variant) throw new Error(`Variant identity changed: ${selection.handle}`);
      if (!Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.available !== 'boolean') throw new Error(`Invalid price or availability: ${selection.handle}`);
      return {
        handle: selection.handle,
        productId: selection.productId,
        variantId: selection.variantId,
        numericVariantId: selection.numericVariantId,
        title: result.data.title,
        option: variant.title,
        priceMinor: variant.price,
        expectedPriceMinor: selection.expectedPriceMinor,
        available: Boolean(variant.available),
      };
    }));
    return {
      currency: 'EGP',
      items,
      totalMinor: items.reduce((sum, item) => sum + item.priceMinor, 0),
      priceChanged: items.some((item) => item.priceMinor !== item.expectedPriceMinor),
      unavailable: items.some((item) => !item.available),
    };
  };

  const quantityFor = (currentCart, variantId) => currentCart.items
    .filter((item) => item.variant_id === variantId)
    .reduce((sum, item) => sum + item.quantity, 0);

  const add = async (selections, { acceptedTotalMinor } = {}) => {
    const fresh = await preflight(selections);
    if (fresh.unavailable) return { status: 'unavailable', fresh };
    if (fresh.priceChanged && acceptedTotalMinor !== fresh.totalMinor) return { status: 'price_changed', fresh };
    const before = await cart();
    const prepared = markers.prepare(fresh.items.map(item => ({ id: item.numericVariantId, quantity: 1 })), before);
    let result;
    try {
      result = await request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: prepared.items }),
      });
    } catch {
      result = { ok: false, status: null };
    }
    let after;
    try { after = await cart(); } catch {
      return { status: 'uncertain', addHttpStatus: result.status, fresh };
    }
    const added = fresh.items.map((item) => ({
      variantId: item.variantId,
      quantityAdded: quantityFor(after, item.numericVariantId) - quantityFor(before, item.numericVariantId),
    }));
    const allAdded = added.every((item) => item.quantityAdded === 1);
    const anyAdded = added.some((item) => item.quantityAdded > 0);
    return {
      status: result.ok && allAdded ? 'success' : anyAdded ? 'partial_or_uncertain' : result.ok || result.status === null ? 'uncertain' : 'failed',
      addHttpStatus: result.status,
      added,
      attribution: markers.readback(prepared, before, after),
      cartItemCount: after.item_count,
      cartTotalMinor: after.total_price,
      fresh,
    };
  };

  return { preflight, add };
}
